// src/components/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BrandName from './BrandName';
import { PlayStoreBadge } from './InstallApp';
import { AREAS } from '../data/areas';
import { GUIDES } from '../data/guides';
import { SOCIAL_LINKS } from '../data/site';
import './Footer.css';

function SocialIcon({ name }) {
  const common = {
    viewBox: '0 0 24 24',
    'aria-hidden': true,
    focusable: 'false',
  };

  if (name === 'Instagram') {
    return (
      <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  if (name === 'TikTok') {
    return (
      <svg {...common} fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    );
  }

  if (name === 'Facebook') {
    return (
      <svg {...common} fill="currentColor">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    );
  }

  return (
    <svg {...common} fill="currentColor">
      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
    </svg>
  );
}

const Footer = () => {
  const { user } = useAuth();

  const getStartedPath = () => {
    if (!user) return '/signup';
    if (user.type === 'customer') return '/customer-dashboard';
    if (user.type === 'provider') return '/provider-dashboard';
    return '/signup';
  };

  return (
  <footer className="footer">
    <div className="footer-inner">
      <div className="footer-brand">
        <div className="footer-logo"><BrandName /></div>
        <p className="footer-tagline">Waste & removals marketplace.<br />Compete. Recycle. Rise.</p>
        <nav className="footer-social" aria-label="Social media">
          {SOCIAL_LINKS.map((link) => (
            <a
              key={link.name}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={link.name}
            >
              <SocialIcon name={link.name} />
            </a>
          ))}
        </nav>
        <div className="footer-get-app">
          <PlayStoreBadge />
        </div>
      </div>
      <div className="footer-links">
        <div className="footer-col">
          <h4>Services</h4>
          <Link to="/services#waste-collection">Waste Collection</Link>
          <Link to="/services#man-and-van">Man & Van</Link>
          <Link to="/services#house-removals">House Removals</Link>
          <Link to="/services#garden-clearance">Garden Clearance</Link>
        </div>
        <div className="footer-col">
          <h4>Platform</h4>
          <Link to={getStartedPath()}>Get Started</Link>
          <Link to="/login">Login</Link>
          <Link to="/provider-signup">Become a Carrier</Link>
        </div>
        <div className="footer-col">
          <h4>Contact</h4>
          <a href="mailto:info@thewastegame.co.uk">info@thewastegame.co.uk</a>
          <span>Swansea, Wales</span>
        </div>
        <div className="footer-col">
          <h4>Guides</h4>
          <Link to="/guides">All guides</Link>
          {GUIDES.map((guide) => (
            <Link key={guide.slug} to={guide.path}>
              {guide.slug === 'check-a-waste-carrier-licence' && 'Check a carrier registration'}
              {guide.slug === 'rubbish-removal-cost-swansea' && 'Rubbish removal cost'}
              {guide.slug === 'free-scrap-collection-swansea' && 'Free scrap collection'}
            </Link>
          ))}
        </div>
        <div className="footer-col">
          <h4>Areas</h4>
          <Link to="/areas">All areas</Link>
          {AREAS.map((area) => (
            <Link key={area.slug} to={area.path}>{area.name}</Link>
          ))}
        </div>
        <div className="footer-col">
          <h4>Legal</h4>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/carrier-agreement">Carrier Agreement</Link>
        </div>
      </div>
    </div>
    <div className="footer-bottom">
      <span>© {new Date().getFullYear()} THE WASTE<span style={{color:'#3ecfa0'}}>GAME</span>. All rights reserved.</span>
      <div className="footer-eco">
        <span className="eco-dot" /> Eco-Powered Platform
      </div>
    </div>
  </footer>
  );
};

export default Footer;
